import {
  ClaimAssigneeRelationshipCanon,
  GetAssignmentsForAssignableResponse,
} from '@eigtech/assignments-types'
import { GetClaimDetailsResponse } from '@eigtech/claims-v2-types'
import { ContactCorn } from '@eigtech/contacts-types'
import { ensureClaimCorn } from '@eigtech/shared-corn-helper'
import { ClaimSummary, GetClaimSummariesResponse } from '@eigtech/summarizer-types'
import { useMutation, useQueryClient } from '@eigtech/ui-shared-api'
import {
  assignmentsQueryKeys,
  unassign,
  useInvalidateAssignmentsForAssignable,
} from '@eigtech/ui-shared-assignments'
import { getLog } from '@eigtech/ui-shared-logging'
import { useCallback } from 'react'
import { claimsQueryKeys } from '../claims/constants'
import { useInvalidateClaim, useInvalidateClaimsSummaries } from '../claims/invalidateClaims'

const log = getLog()

export type UnassignContactFromClaimProps = {
  assigneeRelationship: ClaimAssigneeRelationshipCanon
  claimNumber: string
  contactId: ContactCorn
}

function useOptimisticallyUpdateUnassignedClaimContact() {
  const queryClient = useQueryClient()
  const invalidateClaimsSummaries = useInvalidateClaimsSummaries()
  const invalidateClaim = useInvalidateClaim()
  const invalidateAssignmentsForClaim = useInvalidateAssignmentsForAssignable()

  const onMutate = useCallback(
    async ({
      claimNumber,
      contactId,
      type,
    }: {
      claimNumber: string
      contactId: ContactCorn
      type: ClaimAssigneeRelationshipCanon
    }) => {
      const claimCorn = ensureClaimCorn(claimNumber)

      // update claims list
      const listQueryKey = claimsQueryKeys.list()
      await queryClient.cancelQueries({ queryKey: listQueryKey })

      const previousClaims = queryClient.getQueriesData<GetClaimSummariesResponse>({
        queryKey: listQueryKey,
      })

      previousClaims.forEach(([key, data]) => {
        if (!data) return

        queryClient.setQueryData<GetClaimSummariesResponse>(key, {
          ...data,
          claims: (data?.claims ?? []).map((claim) =>
            getClaimSummaryAfterUnassignment(claim, type, contactId)
          ),
        })
      })

      // update claim detail
      const detailQueryKey = claimsQueryKeys.detail(claimNumber)
      await queryClient.cancelQueries({ queryKey: detailQueryKey })

      const previousClaimDetail = queryClient.getQueryData<GetClaimDetailsResponse>(detailQueryKey)

      if (previousClaimDetail) {
        queryClient.setQueryData<GetClaimDetailsResponse>(detailQueryKey, {
          ...previousClaimDetail,
          [type]: undefined,
        })
      }

      const assignmentQueryKey = assignmentsQueryKeys.assignable.assignments(claimCorn)
      await queryClient.cancelQueries({ queryKey: assignmentQueryKey })
      const previousAssignment =
        queryClient.getQueryData<GetAssignmentsForAssignableResponse>(assignmentQueryKey)

      if (previousAssignment) {
        const updatedAssignment = [
          ...previousAssignment.filter((assignment) => {
            const isClaim = assignment.assignable.type === 'claim'
            const isMatchingContact =
              assignment.assignee.type === 'contact'
                ? assignment.assignee.contact.contactId === contactId &&
                  assignment.assignable.assigneeRelationship === type
                : false

            return isClaim && !isMatchingContact
          }),
        ]

        queryClient.setQueryData<GetAssignmentsForAssignableResponse>(
          assignmentQueryKey,
          updatedAssignment
        )
      }

      return {
        previousClaims,
        previousClaimDetail,
      }
    },
    [queryClient]
  )

  const onError = useCallback(
    (
      error: unknown,
      { claimNumber }: { claimNumber: string },
      context: Awaited<ReturnType<typeof onMutate>> | undefined
    ) => {
      log.error('could not remove assignee', { error })

      const claimCorn = ensureClaimCorn(claimNumber)

      context?.previousClaims.forEach(([key, data]) => {
        queryClient.setQueryData(key, data)
      })

      if (context?.previousClaimDetail?.claimNumber) {
        queryClient.setQueryData(
          claimsQueryKeys.detail(context.previousClaimDetail.claimNumber),
          context.previousClaimDetail
        )
      }

      invalidateClaimsSummaries()
      invalidateClaim(claimNumber)
      invalidateAssignmentsForClaim(claimCorn)
    },
    [invalidateAssignmentsForClaim, invalidateClaim, invalidateClaimsSummaries, queryClient]
  )

  const onSuccess = useCallback(
    (claimNumber: string) => {
      const claimCorn = ensureClaimCorn(claimNumber)

      // invalidate after a few seconds on success
      // (it can take a lil bit for events on backend to catch up)
      setTimeout(() => {
        invalidateAssignmentsForClaim(claimCorn)
        invalidateClaimsSummaries()
        invalidateClaim(claimNumber)
      }, 30000)
    },
    [invalidateAssignmentsForClaim, invalidateClaim, invalidateClaimsSummaries]
  )

  return { onMutate, onError, onSuccess }
}

export function useUnassignContactFromClaim() {
  const { onError, onMutate, onSuccess } = useOptimisticallyUpdateUnassignedClaimContact()

  return useMutation({
    mutationFn: ({
      claimNumber,
      contactId,
      assigneeRelationship,
    }: UnassignContactFromClaimProps) => {
      const claimCorn = ensureClaimCorn(claimNumber)

      return unassign({
        assignableId: claimCorn,
        assigneeId: contactId,
        assigneeRelationship,
      })
    },
    onMutate({ claimNumber, contactId, assigneeRelationship }) {
      return onMutate({
        claimNumber,
        contactId,
        type: assigneeRelationship,
      })
    },
    onSuccess(__, { claimNumber }) {
      onSuccess(claimNumber)
    },
    onError: (error, variables, context) => onError(error, variables, context),
  })
}

function getClaimSummaryAfterUnassignment(
  claim: Partial<ClaimSummary>,
  type: ClaimAssigneeRelationshipCanon,
  contactId: ContactCorn
): Partial<ClaimSummary> {
  const claimHasAssignee = type in claim
  if (!claimHasAssignee) {
    return claim
  }
  // ClaimSummary structure uses assignee relationships as keys and their values are objects with an id property.
  // This is a bit of a hack to get the assignee object.
  const assignee = (claim as unknown as Record<string, { id?: ContactCorn } | undefined>)[type]
  if (assignee?.id && assignee.id === contactId) {
    return {
      ...claim,
      [type]: undefined,
    }
  }
  return claim
}
